import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Search, Route, Calendar, Clock, 
  MapPin, User, Car, DollarSign, Filter
} from 'lucide-react';
import { rideService } from '@/services/rideService';
import type { Ride } from '@/types';

const OwnerRides: React.FC = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState<Ride[]>([]);
  const [filteredRides, setFilteredRides] = useState<Ride[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadRides();
  }, []);

  useEffect(() => {
    let filtered = rides;
    
    if (searchTerm) {
      filtered = filtered.filter(r => 
        r.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.passengerId.includes(searchTerm)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    setFilteredRides(filtered);
  }, [searchTerm, statusFilter, rides]);

  const loadRides = async () => {
    try {
      const data = await rideService.getAllRides(100);
      setRides(data);
      setFilteredRides(data);
    } catch (error) {
      console.error('Error loading rides:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      searching: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'جاري البحث' },
      driver_assigned: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'تم التعيين' },
      driver_arrived: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'السائق واصل' },
      in_progress: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'جارية' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'مكتملة' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'ملغاة' },
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
  };

  const stats = {
    total: rides.length,
    completed: rides.filter(r => r.status === 'completed').length,
    cancelled: rides.filter(r => r.status === 'cancelled').length,
    active: rides.filter(r => ['searching', 'driver_assigned', 'driver_arrived', 'in_progress'].includes(r.status)).length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/owner/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">الرحلات</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">إجمالي الرحلات</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">مكتملة</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">ملغاة</p>
            <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">نشطة</p>
            <p className="text-2xl font-bold text-primary-600">{stats.active}</p>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="البحث برقم الرحلة..."
              className="w-full bg-white border border-gray-200 rounded-xl py-3 pr-12 pl-4 focus:outline-none focus:border-primary-500 transition-colors"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
          >
            <option value="all">كل الحالات</option>
            <option value="searching">جاري البحث</option>
            <option value="driver_assigned">تم التعيين</option>
            <option value="in_progress">جارية</option>
            <option value="completed">مكتملة</option>
            <option value="cancelled">ملغاة</option>
          </select>
        </div>

        {/* Rides List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredRides.length === 0 ? (
          <div className="text-center py-12">
            <Route className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">لا يوجد رحلات</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRides.map((ride) => {
              const status = getStatusBadge(ride.status);
              return (
                <div key={ride.id} className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">رقم الرحلة</p>
                      <p className="font-mono font-medium">#{ride.id.slice(-8).toUpperCase()}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Locations */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">نقطة الانطلاق</p>
                        <p className="font-medium">{ride.pickup?.address || 'غير محدد'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">الوجهة</p>
                        <p className="font-medium">{ride.destination?.address || 'غير محدد'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-4 text-sm border-t pt-4">
                    <span className="flex items-center gap-1 text-gray-600">
                      <User className="w-4 h-4" />
                      {ride.passengerId.slice(-8)}
                    </span>
                    {ride.driverId && (
                      <span className="flex items-center gap-1 text-gray-600">
                        <Car className="w-4 h-4" />
                        {ride.driverId.slice(-8)}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      {ride.finalPrice || ride.estimatedPrice} ج.س
                    </span>
                    <span className="flex items-center gap-1 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      {new Date(ride.createdAt).toLocaleDateString('ar-SD')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default OwnerRides;
